import React, { useEffect, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { Printer, ArrowLeft, Loader2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { fetchSheetExam, type SheetExam } from './sheetModel'

// Trang IN phiếu đáp án viết ký tự (mẫu bảng ebook) — /exam/:slug/phieu
// ?ma=101 → phiếu của mã đề 101: số câu IN + QR mang mã đề, khớp đúng file Word của mã đó.
// Phiếu gọn nửa trên tờ A4; mỗi phiếu in trên MỘT trang riêng (giống phiếu Word/PDF xuất từ pmaker).

const chunk = <T,>(arr: T[], n: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n))
  return out
}

// Viền ô 2px: đường kẻ đậm giúp máy quét dò bảng chắc hơn khi ảnh chụp tối / mờ
const cell = 'border-2 border-black text-center align-middle'

const Block: React.FC<{ numbers: number[]; perRow: number; answerHeight: string }> = ({ numbers, perRow, answerHeight }) => {
  const rows = chunk(numbers, perRow)
  return (
    <table className="w-full border-collapse table-fixed">
      <tbody>
        {rows.map((nums, r) => (
          <React.Fragment key={r}>
            <tr>
              {Array.from({ length: perRow }, (_, c) => (
                <td key={c} className={`${cell} py-[0.4mm] text-[13px] leading-none font-semibold`}>{nums[c] ?? ''}</td>
              ))}
            </tr>
            <tr>
              {Array.from({ length: perRow }, (_, c) => (
                <td key={c} className={cell} style={{ height: answerHeight }} />
              ))}
            </tr>
          </React.Fragment>
        ))}
      </tbody>
    </table>
  )
}

const PaperAnswerSheetPage: React.FC = () => {
  const { slug } = useParams()
  const [params, setParams] = useSearchParams()
  const ma = params.get('ma')
  const [exam, setExam] = useState<SheetExam | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    document.title = 'Phiếu đáp án | VatLi365'
    setExam(null); setErr('')
    if (slug) fetchSheetExam(slug, ma).then(setExam).catch((e) => setErr(e.message))
  }, [slug, ma])

  if (err) return <div className="max-w-xl mx-auto p-6 text-rose-600">{err}</div>
  if (!exam) return <div className="p-10 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={18} />Đang tải đề…</div>

  const { TN, DS, TL } = exam.groups
  const sheetUrl = `${window.location.origin}/exam/${exam.slug}?nop=phieu${exam.code ? `&ma=${exam.code}` : ''}`
  const nums = (g: keyof SheetExam['groups']) => exam.groups[g].map((r) => r.number)
  const roman = ['I', 'II', 'III']
  const sections = [
    TN.length ? { title: 'TRẮC NGHIỆM', note: 'Mỗi ô ghi một chữ A, B, C hoặc D', el: <Block numbers={nums('TN')} perRow={10} answerHeight="8mm" /> } : null,
    DS.length ? { title: 'ĐÚNG SAI', note: 'Mỗi ô ghi 4 ký tự Đ hoặc S theo thứ tự ý a, b, c, d', el: <Block numbers={nums('DS')} perRow={4} answerHeight="8mm" /> } : null,
    TL.length ? { title: 'TRẢ LỜI NGẮN', note: 'Mỗi ô ghi một số, dùng dấu phẩy thập phân (vd 1,2)', el: <Block numbers={nums('TL')} perRow={6} answerHeight="8mm" /> } : null,
  ].filter(Boolean) as { title: string; note: string; el: React.ReactNode }[]

  const sheet = (
    <div className="print-sheet relative w-[210mm] mx-auto bg-white text-black shadow-sm px-[13mm] py-[6mm]"
      style={{ fontFamily: 'Times New Roman, serif' }}>
      {/* 4 ô đen ở góc: giúp máy tìm đúng mép phiếu khi quét (lùi 4 mm để máy in không xén) */}
      {['left-[4mm] top-[4mm]', 'right-[4mm] top-[4mm]', 'left-[4mm] bottom-[4mm]', 'right-[4mm] bottom-[4mm]'].map((pos) => (
        <span key={pos} className={`absolute ${pos} w-[6mm] h-[6mm] bg-black`} />
      ))}

      {/* QR → mở thẳng trang đề + cửa sổ "Nộp bằng phiếu giấy" (quét bằng Zalo hoặc camera) */}
      <div className="flex items-start gap-3 mb-[1.5mm]">
        <div className="flex-1 min-w-0 text-[13.5px] leading-snug">
          <h1 className="text-center text-[16px] leading-tight font-bold uppercase">
            Phiếu đáp án{exam.code && <span className="normal-case"> &nbsp;—&nbsp; Mã đề: {exam.code}</span>}
          </h1>
          <p className="text-center truncate mb-[1mm]">{exam.title}{exam.settings?.subject ? ` — Môn: ${exam.settings.subject}` : ''}</p>
          <p className="whitespace-nowrap overflow-hidden">Họ và tên: ............................................................ Lớp: .............. Ngày làm: ................</p>
          <p className="text-[11px] italic text-gray-600">Viết IN HOA, rõ ràng, mỗi ô một đáp án; muốn sửa thì gạch bỏ, viết chữ mới bên cạnh.</p>
        </div>
        <div className="shrink-0 text-center">
          <QRCodeSVG value={sheetUrl} size={72} level="M" includeMargin={false} />
          <p className="text-[9px] leading-tight w-[72px]">Quét mã để nộp phiếu</p>
        </div>
      </div>

      {sections.map((s, i) => (
        <section key={s.title} className="mb-[1.5mm]">
          <h2 className="text-[13.5px] font-bold mb-[0.8mm]">
            {roman[i]}. {s.title}
            <span className="ml-2 text-[11px] font-normal italic text-gray-600">({s.note})</span>
          </h2>
          {s.el}
        </section>
      ))}

      {/* Dòng chấm để HS tự ghi kinh nghiệm sau khi làm bài */}
      <div className="text-[13px]">
        <div className="flex items-end gap-1 h-[5.5mm]">
          <b className="shrink-0">Kinh nghiệm:</b>
          <span className="flex-1 border-b border-dotted border-black mb-[0.8mm]" />
        </div>
        {[0, 1].map((k) => <div key={k} className="h-[6.5mm] border-b border-dotted border-black" />)}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#fdfcf0] py-6 px-4">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area { position: absolute; left: 0; top: 0; width: 210mm; overflow: visible !important; }
          .print-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print max-w-[210mm] mx-auto mb-4 flex flex-wrap items-center gap-2">
        <Link to={`/exam/${exam.slug}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50">
          <ArrowLeft size={15} /> Về trang đề
        </Link>
        <button onClick={() => window.print()} className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-bold hover:bg-teal-700">
          <Printer size={15} /> In phiếu
        </button>
      </div>

      {/* Đề có mã đề: chọn đúng mã để in phiếu — mỗi mã một phiếu, QR mang mã đề */}
      {exam.codes.length > 0 && (
        <div className="no-print max-w-[210mm] mx-auto mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm text-amber-900 mb-2">Đề có <b>{exam.codes.length} mã đề</b>. Chọn mã để in phiếu tương ứng (phát phiếu mã nào thì kèm đề in mã đó):</p>
          <div className="flex flex-wrap gap-2">
            {exam.codes.map((c) => (
              <button key={c} onClick={() => setParams({ ma: c })}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold ${exam.code === c ? 'bg-amber-500 text-white' : 'bg-white border border-amber-300 text-amber-900 hover:bg-amber-100'}`}>Mã {c}</button>
            ))}
            <button onClick={() => setParams({})}
              className={`px-3 py-1.5 rounded-lg text-sm font-bold ${!exam.code ? 'bg-slate-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}>Đề gốc</button>
          </div>
        </div>
      )}

      {/* Phiếu luôn rộng 210 mm (đo chiều cao đúng như khi in); màn hẹp thì cuộn ngang trong khung */}
      <div className="print-area overflow-x-auto">
        {sheet}
      </div>
    </div>
  )
}

export default PaperAnswerSheetPage
